import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { PhoneFrame } from "./PhoneFrame";

const AMOUNTS = [50000, 100000, 200000];
const GATEWAYS = ["QRIS", "Virtual Account", "E-Wallet"];
const rp = (n) => `Rp ${n.toLocaleString("id-ID")}`;

const QR = ({ seedNum }) => {
  const cells = useMemo(() => {
    let s = seedNum;
    return Array.from({ length: 21 * 21 }, () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280 > 0.52;
    });
  }, [seedNum]);
  const finder = (x, y) => [[0, 0], [14, 0], [0, 14]].some(([fx, fy]) => x >= fx && x < fx + 7 && y >= fy && y < fy + 7);
  const finderOn = (x, y) => {
    const fx = x >= 14 ? 14 : 0;
    const fy = y >= 14 ? 14 : 0;
    const dx = x - fx, dy = y - fy;
    return dx === 0 || dx === 6 || dy === 0 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4);
  };
  return (
    <svg viewBox="0 0 21 21" className="h-full w-full" shapeRendering="crispEdges">
      {cells.map((on, i) => {
        const x = i % 21, y = Math.floor(i / 21);
        const fill = finder(x, y) ? finderOn(x, y) : on;
        return fill ? <rect key={i} x={x} y={y} width="1" height="1" fill="#07080b" /> : null;
      })}
    </svg>
  );
};

const token = () => Array.from({ length: 5 }, () => String(Math.floor(1000 + Math.random() * 9000))).join(" ");

export const QrisPay = () => {
  const [amount, setAmount] = useState(100000);
  const [gw, setGw] = useState("QRIS");
  const [stage, setStage] = useState("idle");
  const [tok, setTok] = useState("");

  const pay = () => {
    setStage("processing");
    setTimeout(() => {
      setTok(token());
      setStage("done");
    }, 1300);
  };

  return (
    <PhoneFrame testId="mockup-qris-payment">
      <div className="px-4 pb-2 pt-9">
        <p className="font-mono-d text-[10.5px] uppercase tracking-[0.18em] text-zinc-400">Kasir digital · Meter 1420-88</p>
        <p className="mt-1 font-display text-2xl font-bold text-zinc-50" data-testid="qris-amount">{rp(amount)}</p>
        <p className="text-xs text-zinc-400">≈ {Math.round(amount / 1500)} kWh</p>
      </div>
      <div className="flex gap-1.5 px-4">
        {AMOUNTS.map((a) => (
          <button key={a} onClick={() => { setAmount(a); setStage("idle"); }} data-testid={`qris-amount-${a}`} className={`flex-1 rounded-lg py-1.5 text-xs transition-colors ${a === amount ? "bg-[var(--dash)] text-black" : "bg-white/[0.05] text-zinc-200 hover:bg-white/10"}`}>
            {a / 1000}k
          </button>
        ))}
      </div>
      <div className="relative mx-4 mt-3 flex flex-1 items-center justify-center rounded-2xl bg-white p-4">
        <AnimatePresence initial={false}>
          {stage !== "done" ? (
            <motion.div key="qr" exit={{ opacity: 0, scale: 0.9 }} className="absolute inset-4 flex flex-col items-center justify-center gap-2">
              <p className="text-[11px] font-bold tracking-[0.3em] text-black">{gw === "QRIS" ? "QRIS" : gw.toUpperCase()}</p>
              <div className="aspect-square w-full max-w-[150px]" data-testid="qris-code"><QR seedNum={amount + gw.length} /></div>
              {stage === "processing" && (
                <div className="absolute inset-0 grid place-items-center rounded-xl bg-white/85">
                  <Loader2 className="animate-spin text-[var(--dash)]" size={30} />
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="ok" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="absolute inset-4 flex flex-col items-center justify-center gap-2 text-center" data-testid="qris-success">
              <CheckCircle2 size={40} className="text-emerald-500" />
              <p className="text-sm font-bold text-black">Paid · token sent</p>
              <p className="font-mono-d text-[11px] text-zinc-700" data-testid="qris-token">{tok}</p>
              <p className="text-[11px] text-zinc-600">Auto-delivered to meter</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="flex gap-1.5 px-4 pt-3">
        {GATEWAYS.map((g) => (
          <button key={g} onClick={() => { setGw(g); setStage("idle"); }} data-testid={`qris-gateway-${g.replace(/\s/g, "-").toLowerCase()}`} className={`flex-1 rounded-full border px-1 py-1 text-[10.5px] transition-colors ${g === gw ? "border-[var(--dash)] text-[var(--dash)]" : "border-white/10 text-zinc-400 hover:text-zinc-100"}`}>
            {g}
          </button>
        ))}
      </div>
      <div className="p-4">
        <button onClick={stage === "done" ? () => setStage("idle") : pay} disabled={stage === "processing"} data-testid="qris-pay-button" className="w-full rounded-xl bg-[var(--dash)] py-2.5 text-sm font-semibold text-black transition-[background-color,transform] hover:bg-[#ff8a33] active:scale-[0.98] disabled:opacity-60">
          {stage === "done" ? "New payment" : stage === "processing" ? "Processing…" : "Simulate scan & pay"}
        </button>
      </div>
    </PhoneFrame>
  );
};
